/**
 * Centralized Color Scheme Configuration
 * 
 * Light-first design. Scheme color provides accent personality
 * (borders, shadows, buttons, highlights) on clean white backgrounds.
 */

const createScheme = (primary, primaryDark, primaryLight, primaryHover) => {
  const r = parseInt(primary.slice(1, 3), 16);
  const g = parseInt(primary.slice(3, 5), 16);
  const b = parseInt(primary.slice(5, 7), 16);

  return {
    primary,
    primaryDark,
    primaryLight,
    primaryHover,
    gradient: [primary, primaryDark],

    // Light mode accents
    accentSoft: `rgba(${r},${g},${b},0.07)`,
    accentBorder: `rgba(${r},${g},${b},0.14)`,
    accentHover: `rgba(${r},${g},${b},0.12)`,
    accentShadow: `rgba(${r},${g},${b},0.08)`,
    accentGlow: `rgba(${r},${g},${b},0.18)`,

    // For user bubbles
    userBubbleBg: primary,
    userBubbleText: '#fff',

    // Bot bubbles
    botBubbleBg: '#f8f9fb',
    botBubbleBorder: '#eef1f5',
    botBubbleText: '#334155',

    // General text
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    textMuted: '#64748b',
    textFaint: '#94a3b8',

    // Surfaces
    bgPage: '#eef0f4',
    bgWidget: '#ffffff',
    bgStrip: 'linear-gradient(175deg,#fafbfc 0%,#f3f4f7 100%)',
    bgCard: '#ffffff',
    bgInput: '#ffffff',
    bgHover: '#f8f9fb',
    divider: '#edf0f4',
    dividerSoft: '#f1f3f7',
  };
};

export const colorSchemes = {
  red: createScheme('#dc3545', '#a52834', '#ff6b6b', '#e64553'),
  blue: createScheme('#3578c4', '#2d5a87', '#5ba8d8', '#4088d4'),
  green: createScheme('#28a745', '#1e7e34', '#5fd068', '#34b555'),
  orange: createScheme('#fd7e14', '#c96000', '#ffab5c', '#ff8c2a'),
  cyan: createScheme('#17a2b8', '#117a8b', '#4dd0e1', '#1fb8d0'),
  purple: createScheme('#6f42c1', '#5a32a3', '#a78bda', '#7d52cf'),
  pink: createScheme('#e83e8c', '#c21766', '#f06daa', '#ec559a'),
  yellow: createScheme('#e6a700', '#c08d00', '#ffd54f', '#f0b400'),
};

export const getColorScheme = (scheme = 'blue') => {
  return colorSchemes[scheme] || colorSchemes.blue;
};

export const getSchemeColor = (scheme = 'blue', colorKey) => {
  const colors = getColorScheme(scheme);
  return colors[colorKey] || colors.primary;
};

export const getGradient = (scheme = 'blue') => {
  const colors = getColorScheme(scheme);
  return colors.gradient;
};

export const getMessageBubbleColor = (scheme = 'blue', type) => {
  const colors = getColorScheme(scheme);
  switch (type) {
    case 'user': return colors.userBubbleBg;
    case 'gpt': return colors.botBubbleBg;
    case 'system': return colors.botBubbleBg;
    default: return colors.botBubbleBg;
  }
};

export const gradientByScheme = Object.fromEntries(
  Object.entries(colorSchemes).map(([key, value]) => [key, value.gradient])
);

export default colorSchemes;
